import { useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';

import { cx } from '../../lib/class-names';

import styles from './AuthForm.module.css';
import { EyeIcon, LockIcon, MailIcon } from './AuthIcons';

interface AuthFormFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> {
  label: string;
  inputType?: 'email' | 'password' | 'text';
  icon?: 'mail' | 'lock';
  hint?: ReactNode;
  error?: string;
  allowPasswordToggle?: boolean;
}

/**
 * Auth-styled labelled input — matches the approved Login field treatment.
 */
export function AuthFormField({
  label,
  inputType = 'text',
  icon,
  hint,
  error,
  allowPasswordToggle = false,
  className,
  required,
  ...inputProps
}: AuthFormFieldProps): React.JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const [visible, setVisible] = useState(false);

  const isPassword = inputType === 'password';
  const resolvedType = isPassword && allowPasswordToggle && visible ? 'text' : inputType;

  const describedBy =
    [hint === undefined ? null : hintId, error === undefined ? null : errorId]
      .filter((value): value is string => value !== null)
      .join(' ') || undefined;

  const LeadingIcon = icon === 'mail' ? MailIcon : icon === 'lock' ? LockIcon : null;

  return (
    <div className={cx(styles.field, className)}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required === true && <span className="visually-hidden"> (required)</span>}
      </label>

      <div
        className={cx(styles.inputShell, isPassword && allowPasswordToggle && styles.passwordShell)}
      >
        {LeadingIcon !== null && <LeadingIcon />}
        <input
          {...inputProps}
          id={id}
          className={styles.input}
          type={resolvedType}
          required={required}
          aria-invalid={error === undefined ? undefined : true}
          aria-describedby={describedBy}
        />
        {isPassword && allowPasswordToggle && (
          <button
            type="button"
            className={styles.togglePassword}
            aria-pressed={visible}
            aria-label="Toggle visibility"
            title={visible ? 'Hide password' : 'Show password'}
            onClick={() => {
              setVisible((current) => !current);
            }}
          >
            <EyeIcon open={visible} />
          </button>
        )}
      </div>

      {hint !== undefined && (
        <p className={styles.hint} id={hintId}>
          {hint}
        </p>
      )}

      {error !== undefined && (
        <p className={styles.fieldError} id={errorId} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
